import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, ShieldCheck } from 'lucide-react'
import { Field } from '@/pages/vault/VaultForms'
import {
  accountEnrollment,
  AccountCommandError,
  newAccountBootstrap,
  validAccountReference,
  type AccountBootstrap,
  type AccountPurpose,
  type AccountProgress,
  type AccountMfaSetup,
} from '@/services/vault/accountEnrollment'

interface Props {
  purpose: AccountPurpose
}
interface PendingCommand {
  kind: 'start' | 'verify' | 'setup' | 'finish' | 'password'
  acceptanceVersion?: string
  send: () => Promise<AccountProgress | AccountMfaSetup>
}

export default function IndependentAccountPage({ purpose }: Props) {
  const recovery = purpose === 'recovery'
  const [email, setEmail] = useState('')
  const [previous, setPrevious] = useState('')
  const [reference, setReference] = useState('')
  const [progress, setProgress] = useState<AccountProgress | null>(null)
  const [setup, setSetup] = useState<AccountMfaSetup | null>(null)
  const [proof, setProof] = useState('')
  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [code, setCode] = useState('')
  const [savedCodes, setSavedCodes] = useState(false)
  const [accepted, setAccepted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [pendingKind, setPendingKind] = useState<PendingCommand['kind'] | null>(null)
  const [resetAcknowledged, setResetAcknowledged] = useState(false)
  const bootstrap = useRef<AccountBootstrap | null>(null)
  const pending = useRef<PendingCommand | null>(null)
  const running = useRef(false)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      bootstrap.current = null
      pending.current = null
    }
  }, [])

  const clearPrivateFields = () => {
    setPassword('')
    setConfirmPassword('')
    setProof('')
    setCode('')
    setSetup(null)
    setFullName('')
    setEmail('')
    setPrevious('')
    setAccepted(false)
    setSavedCodes(false)
  }
  const clearPending = () => {
    pending.current = null
    setPendingKind(null)
  }
  const applyProgress = (result: AccountProgress) => {
    if (progress?.terms_version !== result.terms_version) setAccepted(false)
    setProgress(result)
    if (result.status === 'complete') {
      clearPending()
      clearPrivateFields()
      bootstrap.current = null
      setNotice(
        recovery
          ? 'Password reset confirmed. All previous sessions have ended.'
          : 'Your independent account is ready. Sign in with a fresh authenticator code.'
      )
    }
    if (result.status === 'superseded') {
      clearPending()
      clearPrivateFields()
      bootstrap.current = null
      setNotice('This request was replaced. Continue using the new request reference.')
    }
  }

  const execute = async (operation: () => Promise<void>, clearDefiniteFailure = false) => {
    if (running.current) return
    running.current = true
    setBusy(true)
    setError('')
    setNotice('')
    try {
      await operation()
    } catch (failure) {
      if (!mounted.current) return
      // Never render raw transport/validation errors containing submitted secrets.
      setError(
        failure instanceof AccountCommandError
          ? failure.message
          : 'The result could not be confirmed. Keep this page open and check the request status.'
      )
      if (
        clearDefiniteFailure &&
        failure instanceof AccountCommandError &&
        !failure.uncertain &&
        failure.status !== 409
      ) {
        clearPending()
        setProof('')
        setCode('')
      }
    } finally {
      running.current = false
      if (mounted.current) setBusy(false)
    }
  }
  const sendPending = async (command: PendingCommand) => {
    const result = await command.send()
    if (!mounted.current) return
    if (command.kind === 'setup') {
      clearPending()
      setSetup(result as AccountMfaSetup)
      setPassword('')
      setConfirmPassword('')
      setFullName('')
      setSavedCodes(false)
      setAccepted(false)
      setProgress(current => (current ? { ...current, status: 'mfa_setup' } : current))
    } else {
      const state = result as AccountProgress
      const validStatus =
        command.kind === 'verify'
          ? 'email_verified'
          : command.kind === 'finish' || command.kind === 'password'
            ? 'complete'
            : 'awaiting_email'
      if (state.status !== validStatus)
        throw new AccountCommandError(
          'The result did not match this step. Check the request status.',
          true
        )
      clearPending()
      applyProgress(state)
      setProof('')
      setCode('')
      if (command.kind === 'start')
        setNotice(
          'If this request is eligible, an email proof will be sent. Check your inbox and spam folder.'
        )
    }
  }
  const submit = (command: PendingCommand) => {
    if (running.current || pending.current) return
    pending.current = command
    setPendingKind(command.kind)
    void execute(() => sendPending(command), true)
  }
  const start = () => {
    if (previous.trim() && !validAccountReference(previous.trim())) {
      setError('Enter the exact previous request reference.')
      return
    }
    try {
      // A definitive failed start reuses its original bootstrap until explicitly reset.
      bootstrap.current ??= newAccountBootstrap()
      const body = {
        ...bootstrap.current,
        email: email.trim(),
        ...(previous.trim() ? { previous_command_id: previous.trim() } : {}),
      }
      setReference(body.command_id)
      submit({ kind: 'start', send: () => accountEnrollment.start(purpose, body) })
    } catch {
      setError('Secure account requests require a supported browser over HTTPS.')
    }
  }
  const checkStatus = () => {
    const body = bootstrap.current
    if (!body) return
    void execute(async () => {
      const result = await accountEnrollment.status(purpose, body)
      if (!mounted.current) return
      const kind = pending.current?.kind
      const acceptanceChanged =
        kind === 'finish' &&
        result.status === 'mfa_setup' &&
        pending.current?.acceptanceVersion !== result.terms_version
      applyProgress(result)
      if (acceptanceChanged) {
        clearPending()
        setCode('')
        setAccepted(false)
        setNotice(
          'The current account acceptance version changed. Review it and provide a fresh authenticator code.'
        )
        return
      }
      if (
        (kind === 'start' && result.status === 'awaiting_email') ||
        (kind === 'verify' && result.status === 'email_verified')
      ) {
        clearPending()
        setProof('')
      }
      if (result.status !== 'complete' && result.status !== 'superseded')
        setNotice('Request status checked. Continue with the current step.')
    })
  }
  const resend = () => {
    const body = bootstrap.current
    if (!body || pending.current) return
    setProof('')
    void execute(async () => {
      const result = await accountEnrollment.resend(purpose, body)
      if (!mounted.current) return
      applyProgress(result)
      setProof('')
      setNotice(
        'A new proof was requested if eligible. Use the newest email; earlier proofs are replaced.'
      )
    })
  }
  const credentials = (event: React.FormEvent) => {
    event.preventDefault()
    const body = bootstrap.current
    if (!body) return
    if (password !== confirmPassword) {
      setError('The passwords do not match.')
      return
    }
    if (recovery) {
      const frame = { ...body, password, mfa_code: code.trim() }
      submit({ kind: 'password', send: () => accountEnrollment.password(frame) })
    } else {
      const frame = { ...body, password, full_name: fullName.trim() }
      submit({ kind: 'setup', send: () => accountEnrollment.setup(frame) })
    }
  }
  const reset = () => {
    if (running.current || !resetAcknowledged) return
    const oldReference = reference
    clearPending()
    clearPrivateFields()
    bootstrap.current = null
    setProgress(null)
    setReference('')
    setPrevious(!recovery && progress?.status === 'mfa_setup' ? oldReference : '')
    setResetAcknowledged(false)
    setError('')
    setNotice(
      recovery
        ? 'Start a fresh email-proof request to recover your independent account.'
        : 'Start a fresh email-proof request. To resume pending setup, enter its exact previous reference and original account details.'
    )
  }

  const complete = progress?.status === 'complete'
  const closed = complete || progress?.status === 'superseded'
  const waitingEmail = progress?.status === 'awaiting_email'
  const needsCredentials =
    progress?.status === 'email_verified' || (progress?.status === 'mfa_setup' && !setup)
  const locked = busy || !!pendingKind

  return (
    <main className="min-h-screen bg-surface-light dark:bg-surface-dark text-text-primary px-4 py-8 sm:py-12">
      <section
        className="card card-elevated mx-auto max-w-2xl p-6 sm:p-8 space-y-6 min-w-0"
        aria-busy={busy}
      >
        <Link
          to="/login?account=independent"
          className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400"
        >
          <ArrowLeft className="w-4 h-4" /> Independent account sign-in
        </Link>
        <div className="space-y-2">
          <ShieldCheck
            className="w-8 h-8 text-primary-600 dark:text-primary-400"
            aria-hidden="true"
          />
          <h1 className="text-2xl font-semibold">
            {recovery ? 'Recover your independent account' : 'Create your independent account'}
          </h1>
          <p className="text-text-secondary">
            {recovery
              ? 'Verify your email and use your current authenticator or a backup code to reset your password.'
              : 'Verify your email, secure your account with an authenticator, and review the account acceptance.'}
          </p>
          {!recovery && (
            <p className="text-sm text-text-secondary">
              Your account is personal. Access to a client’s data requires separate verified
              authority and authorization.
            </p>
          )}
        </div>
        {error && (
          <p
            role="alert"
            className="rounded-lg p-4 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-200"
          >
            {error}
          </p>
        )}
        {notice && (
          <p role="status" className="text-sm text-text-secondary">
            {notice}
          </p>
        )}
        {reference && (
          <div className="rounded-lg border border-surface-overlay p-4 space-y-2">
            <p className="font-medium">Request reference</p>
            <p className="font-mono text-sm break-all select-all">{reference}</p>
            <p className="text-sm text-text-secondary">
              {recovery
                ? 'Keep this reference while checking the result. Keep this page open while you continue; after a reload, start a fresh email-proof request.'
                : 'Keep this reference to resume pending setup. Keep this page open while you continue; after a reload you must verify your email again.'}
            </p>
            {bootstrap.current && (
              <button
                type="button"
                className="btn btn-outline"
                disabled={busy}
                onClick={checkStatus}
              >
                Check request status
              </button>
            )}
          </div>
        )}
        {!progress && (
          <form
            className="space-y-4"
            onSubmit={event => {
              event.preventDefault()
              start()
            }}
          >
            <fieldset disabled={locked} className="space-y-4">
              <Field
                label="Email address"
                type="email"
                autoComplete="email"
                required
                maxLength={320}
                value={email}
                onChange={event => setEmail(event.target.value)}
              />
              {!recovery && (
                <Field
                  label="Previous pending request reference (optional)"
                  value={previous}
                  maxLength={36}
                  autoComplete="off"
                  onChange={event => setPrevious(event.target.value)}
                />
              )}
              {!recovery && (
                <p className="text-sm text-text-secondary">
                  Resuming setup also requires the same name and original password. An existing
                  active or firm account cannot be replaced through this form.
                </p>
              )}
              <button className="btn btn-primary" disabled={locked}>
                Request email proof
              </button>
            </fieldset>
          </form>
        )}
        {waitingEmail && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Verify your email</h2>
            <p className="text-sm text-text-secondary">
              Paste the proof from the email for this request reference. Proofs expire; use only the
              latest email after a resend.
            </p>
            <form
              className="space-y-4"
              onSubmit={event => {
                event.preventDefault()
                if (!bootstrap.current) return
                const frame = { ...bootstrap.current, email_proof: proof.trim() }
                submit({
                  kind: 'verify',
                  send: () => accountEnrollment.verifyEmail(purpose, frame),
                })
              }}
            >
              <Field
                label="Email proof"
                required
                pattern="[a-f0-9]{64}"
                maxLength={64}
                autoComplete="off"
                value={proof}
                disabled={locked}
                onChange={event => setProof(event.target.value)}
              />
              <button className="btn btn-primary" disabled={locked}>
                Verify email
              </button>
            </form>
            <button type="button" className="btn btn-outline" disabled={locked} onClick={resend}>
              Request a new email proof
            </button>
            <p className="text-sm text-text-secondary">
              Each new email request replaces older proofs. If a resend result is uncertain, wait
              for the newest email or check status before requesting another; it is never repeated
              automatically.
            </p>
          </div>
        )}
        {needsCredentials && (
          <form className="space-y-4" onSubmit={credentials}>
            <h2 className="text-lg font-semibold">
              {recovery
                ? 'Reset your password'
                : previous
                  ? 'Resume your pending setup'
                  : 'Set up your account'}
            </h2>
            <fieldset disabled={locked} className="space-y-4">
              {!recovery && (
                <Field
                  label="Full name"
                  autoComplete="name"
                  required
                  maxLength={255}
                  value={fullName}
                  onChange={event => setFullName(event.target.value)}
                />
              )}
              <Field
                label={
                  !recovery && previous
                    ? 'Original pending account password'
                    : recovery
                      ? 'New password'
                      : 'Password'
                }
                type="password"
                autoComplete={!recovery && previous ? 'current-password' : 'new-password'}
                required
                minLength={8}
                maxLength={72}
                value={password}
                onChange={event => setPassword(event.target.value)}
              />
              <Field
                label="Confirm password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                maxLength={72}
                value={confirmPassword}
                onChange={event => setConfirmPassword(event.target.value)}
              />
              <p className="text-sm text-text-secondary">
                Use at least 8 characters with upper and lower case letters, a digit and a special
                character, within 72 UTF-8 bytes.
              </p>
              {recovery && (
                <Field
                  label="Current authenticator or backup code"
                  required
                  maxLength={16}
                  autoComplete="one-time-code"
                  value={code}
                  onChange={event => setCode(event.target.value.replace(/\s/g, ''))}
                />
              )}
              <button className="btn btn-primary" disabled={locked}>
                {recovery
                  ? 'Reset password and end previous sessions'
                  : 'Continue to authenticator setup'}
              </button>
            </fieldset>
          </form>
        )}
        {setup && progress?.status === 'mfa_setup' && (
          <form
            className="space-y-4"
            onSubmit={event => {
              event.preventDefault()
              if (!bootstrap.current || !accepted || !savedCodes) return
              const frame = {
                ...bootstrap.current,
                mfa_code: code.trim(),
                accepted: true as const,
                terms_version: progress.terms_version,
              }
              submit({
                kind: 'finish',
                acceptanceVersion: frame.terms_version,
                send: () => accountEnrollment.finish(frame),
              })
            }}
          >
            <h2 className="text-lg font-semibold">Secure and activate your account</h2>
            <p className="text-sm text-text-secondary">
              Add a time-based account named TaxHive in your authenticator using this setup key.
              Keep the key private.
            </p>
            <p
              className="font-mono break-all select-all border border-surface-overlay rounded-lg p-4"
              aria-label="Authenticator setup key"
            >
              {setup.secret}
            </p>
            <h3 className="font-medium">Single-use backup codes</h3>
            <p className="text-sm text-text-secondary">
              Save these codes somewhere secure before activation. They are shown only during setup
              and can be used if your authenticator is unavailable.
            </p>
            <ul
              className="grid grid-cols-1 sm:grid-cols-2 gap-2 font-mono select-all"
              aria-label="Backup codes"
            >
              {setup.backup_codes.map((value, index) => (
                <li key={index}>{value}</li>
              ))}
            </ul>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                required
                checked={savedCodes}
                disabled={locked}
                onChange={event => setSavedCodes(event.target.checked)}
              />
              I have saved my backup codes securely.
            </label>
            <Field
              label="Fresh 6-digit authenticator code"
              required
              pattern="[0-9]{6}"
              maxLength={6}
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              disabled={locked}
              onChange={event => setCode(event.target.value.replace(/\s/g, ''))}
            />
            <div className="rounded-lg border border-surface-overlay p-4 space-y-3">
              <p className="text-sm text-text-secondary">
                Account acceptance version: {progress.terms_version}
              </p>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  required
                  checked={accepted}
                  disabled={locked}
                  onChange={event => setAccepted(event.target.checked)}
                />
                I request this personal independent account, confirm control of the verified email
                and authenticator, and accept responsibility for protecting my credentials and
                recovery codes. This does not appoint me as an owner or representative of any
                client.
              </label>
            </div>
            <button className="btn btn-primary" disabled={locked || !accepted || !savedCodes}>
              Activate my independent account
            </button>
          </form>
        )}
        {pendingKind && (
          <div className="rounded-lg border border-surface-overlay p-4 space-y-3">
            <p className="text-sm text-text-secondary">
              The original step is retained on this page. Check status or deliberately retry it with
              the same fields; a changed request is not sent automatically.
            </p>
            <button
              type="button"
              className="btn btn-outline"
              disabled={busy}
              onClick={() => {
                const command = pending.current
                if (command) void execute(() => sendPending(command), true)
              }}
            >
              Retry original step
            </button>
          </div>
        )}
        {complete && (
          <Link to="/login?account=independent" className="btn btn-primary inline-flex">
            Sign in to your independent account
          </Link>
        )}
        {!closed && reference && (
          <div className="space-y-3 border-t border-surface-overlay pt-4">
            <label className="flex items-start gap-2 text-sm text-text-secondary">
              <input
                type="checkbox"
                checked={resetAcknowledged}
                disabled={busy}
                onChange={event => setResetAcknowledged(event.target.checked)}
              />
              I have kept this reference. A fresh request requires new email proof; any pending or
              uncertain original result may still need to be checked or resumed.
            </label>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy || !resetAcknowledged}
              onClick={reset}
            >
              Start a fresh request
            </button>
          </div>
        )}
        {recovery && !complete && (
          <p className="text-sm text-text-secondary">
            If you have lost both your authenticator and all backup codes, email proof alone cannot
            reset MFA. This recovery form cannot restore that access.
          </p>
        )}
      </section>
    </main>
  )
}
